import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  ShieldCheck,
  Lock,
  PersonCircle,
  Eye,
  EyeSlash,
  ArrowRight,
  Key,
  ExclamationTriangle,
  Server,
} from "react-bootstrap-icons";

import logo from "../../assets/ieo.png";

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState(""); // "network", "server", "credentials"

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorType("");
    setLoading(true);

    try {
      // Validate inputs first
      if (!username.trim() || !password.trim()) {
        setError("Please enter both username and password");
        setErrorType("credentials");
        setLoading(false);
        return;
      }

      const result = await login(username, password);

      if (result.success) {
        navigate("/admin");
      } else {
        // Handle different types of errors
        if (
          result.error?.includes("network") ||
          result.error?.includes("Network")
        ) {
          setErrorType("network");
          setError(
            "Network error. Please check your internet connection and try again.",
          );
        } else if (
          result.error?.includes("500") ||
          result.error?.includes("server")
        ) {
          setErrorType("server");
          setError("Server error. Please try again later or contact support.");
        } else {
          setErrorType("credentials");
          setError(result.error || "Invalid username or password");
        }
      }
    } catch (error) {
      // Catch any unexpected errors
      console.error("Login error:", error);
      setErrorType("server");
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Error alert styling based on error type
  const getAlertVariant = () => {
    switch (errorType) {
      case "network":
        return "warning";
      case "server":
        return "danger";
      case "credentials":
        return "danger";
      default:
        return "danger";
    }
  };

  const getAlertIcon = () => {
    switch (errorType) {
      case "network":
        return <ExclamationTriangle className="me-2" size={18} />;
      case "server":
        return <Server className="me-2" size={18} />;
      case "credentials":
        return <ShieldCheck className="me-2" size={18} />;
      default:
        return <ShieldCheck className="me-2" size={18} />;
    }
  };

  const pageStyle = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  };

  const cardStyle = {
    borderRadius: "20px",
    border: "none",
    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
    overflow: "hidden",
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
  };

  const gradientButtonStyle = {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    borderRadius: "10px",
    padding: "12px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
  };

  const inputStyle = {
    borderRadius: "10px",
    border: "2px solid #e9ecef",
    padding: "12px 16px",
    fontSize: "16px",
    transition: "all 0.3s ease",
  };

  const inputFocusStyle = {
    borderColor: "#667eea",
    boxShadow: "0 0 0 0.2rem rgba(102, 126, 234, 0.25)",
  };

  // Add retry function for network/server errors
  const handleRetry = () => {
    if (errorType === "network" || errorType === "server") {
      setError("");
      setErrorType("");
    }
  };

  return (
    <div style={pageStyle}>
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={5}>
            <Card style={cardStyle}>
              <Card.Body className="p-4 p-md-5">
                <div className="text-center mb-4">
                  <img
                    src={logo}
                    alt="logo"
                    style={{ width: "250px", marginBottom: "20px" }}
                  />
                  <h3 className="fw-bold mb-1">Admin Login</h3>
                  <p className="text-muted mb-0">
                    Secure access to administration panel
                  </p>
                </div>

                {error && (
                  <Alert
                    variant={getAlertVariant()}
                    dismissible
                    onClose={() => {
                      setError("");
                      setErrorType("");
                    }}
                    className="border-0 shadow-sm mb-4"
                    style={{ borderRadius: "10px" }}
                  >
                    <div className="d-flex align-items-center">
                      {getAlertIcon()}
                      <span>{error}</span>
                    </div>

                    {(errorType === "network" || errorType === "server") && (
                      <div className="mt-2">
                        <small className="d-block mb-1">
                          Troubleshooting steps:
                        </small>
                        <ul className="small mb-2 ps-3">
                          {errorType === "network" && (
                            <>
                              <li>Check your internet connection</li>
                              <li>Verify your network settings</li>
                              <li>Try accessing other websites</li>
                            </>
                          )}
                          {errorType === "server" && (
                            <>
                              <li>Server might be temporarily unavailable</li>
                              <li>Try again in a few minutes</li>
                              <li>
                                Contact technical support if issue persists
                              </li>
                            </>
                          )}
                        </ul>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleRetry}
                          className="mt-1"
                        >
                          Try Again
                        </Button>
                      </div>
                    )}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  {/* Username Field */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium mb-2 d-flex align-items-center">
                      <PersonCircle className="me-2" />
                      Username
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          // Clear error when user starts typing
                          if (error && errorType === "credentials") {
                            setError("");
                            setErrorType("");
                          }
                        }}
                        placeholder="Enter your username"
                        required
                        disabled={loading}
                        style={inputStyle}
                        onFocus={(e) =>
                          (e.target.style = {
                            ...inputStyle,
                            ...inputFocusStyle,
                          })
                        }
                        onBlur={(e) => (e.target.style = inputStyle)}
                        className={
                          errorType === "credentials" ? "is-invalid" : ""
                        }
                      />
                      <div
                        className="position-absolute"
                        style={{ top: "12px", right: "15px", color: "#667eea" }}
                      >
                        <PersonCircle size={20} />
                      </div>
                    </div>
                  </Form.Group>

                  {/* Password Field */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium mb-2 d-flex align-items-center">
                      <Lock className="me-2" />
                      Password
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          // Clear error when user starts typing
                          if (error && errorType === "credentials") {
                            setError("");
                            setErrorType("");
                          }
                        }}
                        placeholder="Enter your password"
                        required
                        disabled={loading}
                        style={inputStyle}
                        onFocus={(e) =>
                          (e.target.style = {
                            ...inputStyle,
                            ...inputFocusStyle,
                          })
                        }
                        onBlur={(e) => (e.target.style = inputStyle)}
                        className={
                          errorType === "credentials" ? "is-invalid" : ""
                        }
                      />
                      <div
                        className="position-absolute"
                        style={{ top: "12px", right: "45px", color: "#667eea" }}
                      >
                        <Key size={20} />
                      </div>
                      <Button
                        variant="link"
                        className="position-absolute p-0"
                        style={{ top: "10px", right: "15px", color: "#6c757d" }}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? (
                          <EyeSlash size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </Button>
                    </div>
                  </Form.Group>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    className="w-100 mb-4"
                    disabled={loading || !!error}
                    style={{
                      ...gradientButtonStyle,
                      opacity: loading ? 0.8 : 1,
                      transform: loading ? "scale(0.98)" : "scale(1)",
                      cursor: loading ? "wait" : "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !error) {
                        e.currentTarget.style.transform = "scale(1.02)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = loading
                        ? "scale(0.98)"
                        : "scale(1)";
                    }}
                  >
                    {loading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        <span>Authenticating...</span>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center">
                        <span>Login to Dashboard</span>
                        <ArrowRight className="ms-2" size={20} />
                      </div>
                    )}
                  </Button>

                  {/* Connection Status */}
                  <div className="text-center mb-3">
                    <div className="d-flex align-items-center justify-content-center">
                      <div
                        className={`rounded-circle me-2 ${
                          errorType === "network" || errorType === "server"
                            ? "bg-danger"
                            : "bg-success"
                        }`}
                        style={{ width: "8px", height: "8px" }}
                      />
                      <small className="text-muted">
                        {errorType === "network" || errorType === "server"
                          ? "Connection issues detected"
                          : "Server connection: Normal"}
                      </small>
                    </div>
                  </div>

                  {/* Security Note */}
                  <div className="text-center">
                    <small className="text-muted">
                      <Lock className="me-1" size={12} />
                      Your login is secured with 256-bit SSL encryption
                    </small>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Footer */}
            <div className="text-center mt-4">
              <p className="text-white-50 mb-1">
                &copy; {new Date().getFullYear()} Ireland Education Office
              </p>
              <small className="text-white-50 d-block mb-1">
                v2.1.0 • Privacy Policy • Terms of Service
              </small>
              {errorType === "server" && (
                <small className="text-white-50 d-block">
                  <Server className="me-1" size={12} />
                  Server Status: Experiencing Issues •{" "}
                  <a
                    href="mailto:support@educationireland.net"
                    className="text-white text-decoration-underline"
                  >
                    Contact Support
                  </a>
                </small>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminLogin;
